// Input capture. Prefers an AudioWorklet (audio-thread capture, glitch-free);
// falls back to ScriptProcessor where worklets aren't available. Captures raw
// float samples into an AudioBuffer (no lossy round-trip), with optional monitor.
import { AudioEngine } from "./engine";

export interface InputDevice {
  deviceId: string;
  label: string;
}

export async function listInputs(): Promise<InputDevice[]> {
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach((t) => t.stop());
  } catch { /* ignore — user can still pick default */ }
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === "audioinput")
    .map((d) => ({ deviceId: d.deviceId, label: d.label || "Microphone" }));
}

export async function listOutputs(): Promise<InputDevice[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === "audiooutput")
    .map((d) => ({ deviceId: d.deviceId, label: d.label || "Output" }));
}

interface ITrackRecorder {
  level: number;
  begin(): void;
  finish(): Promise<AudioBuffer | null>;
  dispose(): void;
  livePeaks(): LivePeaks;
  setInputGain(db: number): void;
}

const dbToLin = (db: number) => Math.pow(10, db / 20);

export type InputChannel = "1" | "2" | "stereo";

/** Channels the device actually delivers (1 for a true mono mic). */
function deviceChannels(stream: MediaStream): number {
  const st = stream.getAudioTracks()[0]?.getSettings() as any;
  return Number(st?.channelCount) || 2;
}

/** Wire `source` into `into` (the input-gain node) carrying only the chosen
 *  channel(s). Mono picks one physical input and drops the other — so an unused
 *  second interface input (preamp hiss) never reaches the recording. Returns the
 *  number of channels delivered and any splitter (for disposal). */
function routeInput(ctx: AudioContext, stream: MediaStream, source: AudioNode, into: GainNode, mode: InputChannel): { channels: number; splitter: ChannelSplitterNode | null } {
  const devCh = deviceChannels(stream);
  if (mode === "stereo" && devCh >= 2) {
    into.channelCount = 2; into.channelCountMode = "explicit"; into.channelInterpretation = "speakers";
    source.connect(into);
    return { channels: 2, splitter: null };
  }
  into.channelCount = 1; into.channelCountMode = "explicit"; into.channelInterpretation = "speakers";
  const pick = mode === "2" && devCh >= 2 ? 1 : 0;
  const splitter = ctx.createChannelSplitter(2);
  source.connect(splitter);
  splitter.connect(into, pick, 0);
  return { channels: 1, splitter };
}

export interface LivePeaks { min: number[]; max: number[]; binSamples: number; }

/** Incrementally reduces incoming samples to min/max bins for live waveform draw. */
class PeakAccumulator {
  min: number[] = [];
  max: number[] = [];
  private cMin = 1; private cMax = -1; private n = 0;
  constructor(public binSamples = 256) {}
  reset() { this.min = []; this.max = []; this.cMin = 1; this.cMax = -1; this.n = 0; }
  push(d: Float32Array) {
    for (let i = 0; i < d.length; i++) {
      const v = d[i];
      if (v < this.cMin) this.cMin = v;
      if (v > this.cMax) this.cMax = v;
      if (++this.n >= this.binSamples) { this.min.push(this.cMin); this.max.push(this.cMax); this.cMin = 1; this.cMax = -1; this.n = 0; }
    }
  }
}

const workletReady = new WeakMap<AudioContext, Promise<boolean>>();
function ensureWorklet(ctx: AudioContext): Promise<boolean> {
  let p = workletReady.get(ctx);
  if (!p) {
    p = ctx.audioWorklet
      ? ctx.audioWorklet.addModule("/recorder-worklet.js").then(() => true).catch(() => false)
      : Promise.resolve(false);
    workletReady.set(ctx, p);
  }
  return p;
}

// chunks: one entry per posted block; each block is an array of channel buffers.
function buildBuffer(ctx: AudioContext, chunks: Float32Array[][], channels: number): AudioBuffer | null {
  if (channels === 0) return null;
  const total = chunks.reduce((n, block) => n + (block[0]?.length ?? 0), 0);
  if (total === 0) return null;
  const buf = ctx.createBuffer(channels, total, ctx.sampleRate);
  for (let ch = 0; ch < channels; ch++) {
    const out = buf.getChannelData(ch);
    let off = 0;
    for (const block of chunks) {
      const data = block[ch] ?? block[0];
      out.set(data, off);
      off += block[0].length;
    }
  }
  return buf;
}

/** AudioWorklet-based recorder: capture happens on the audio render thread. */
class WorkletRecorder implements ITrackRecorder {
  private source: MediaStreamAudioSourceNode;
  private node: AudioWorkletNode;
  private sink: GainNode;
  private blocks: Float32Array[][] = []; // per call: [ch0chunk, ch1chunk...]
  private channels = 0;
  private recording = false;
  private eos: (() => void) | null = null;
  private peaks = new PeakAccumulator();
  level = 0;
  /** Audio-clock time of the first captured sample (reported by the worklet). */
  startedAt: number | null = null;

  private inGain: GainNode;
  private splitter: ChannelSplitterNode | null;
  constructor(private ctx: AudioContext, private stream: MediaStream, monitor: AudioNode | null, inputGainDb = 0, mode: InputChannel = "1") {
    this.source = ctx.createMediaStreamSource(stream);
    this.inGain = ctx.createGain();
    this.inGain.gain.value = dbToLin(inputGainDb);
    const r = routeInput(ctx, stream, this.source, this.inGain, mode);
    this.splitter = r.splitter;
    this.node = new AudioWorkletNode(ctx, "recorder-processor", { channelCount: r.channels, channelCountMode: "explicit", channelInterpretation: "speakers" });
    this.sink = ctx.createGain();
    this.sink.gain.value = 0;
    this.inGain.connect(this.node);   // capture is post input-gain
    this.node.connect(this.sink);
    this.sink.connect(ctx.destination);
    if (monitor) this.inGain.connect(monitor);
    this.node.port.onmessage = (e) => {
      const d = e.data;
      if (d.type === "level") { this.level = d.peak; }
      else if (d.type === "started") { this.startedAt = d.time; }
      else if (d.type === "data") {
        this.channels = d.chans.length;
        if (this.recording && d.chans[0]) this.peaks.push(d.chans[0]); // live waveform
        this.blocks.push(d.chans);
      } else if (d.type === "eos") { this.eos?.(); this.eos = null; }
    };
  }

  livePeaks(): LivePeaks { return { min: this.peaks.min, max: this.peaks.max, binSamples: this.peaks.binSamples }; }
  setInputGain(db: number) { this.inGain.gain.value = dbToLin(db); }

  begin() { this.blocks = []; this.channels = 0; this.startedAt = null; this.peaks.reset(); this.recording = true; this.node.port.postMessage("start"); }

  finish(): Promise<AudioBuffer | null> {
    if (!this.recording) return Promise.resolve(null);
    this.recording = false;
    return new Promise((resolve) => {
      const done = () => {
        try { resolve(buildBuffer(this.ctx, this.blocks, this.channels)); }
        catch (e) { console.error("buildBuffer failed", e); resolve(null); }
      };
      this.eos = done;
      this.node.port.postMessage("stop");
      setTimeout(() => { if (this.eos) { this.eos = null; done(); } }, 1000); // safety
    });
  }

  dispose() {
    try { this.node.port.onmessage = null; } catch {}
    try { this.source.disconnect(); } catch {}
    try { this.splitter?.disconnect(); } catch {}
    try { this.inGain.disconnect(); } catch {}
    try { this.node.disconnect(); } catch {}
    try { this.sink.disconnect(); } catch {}
  }
}

/** A standalone capture of one input (used to record our mic for a remote
 *  partner). `startedAt` gives the exact audio-clock time of the first sample.
 *  Null if AudioWorklet isn't available. */
export async function createCapture(ctx: AudioContext, stream: MediaStream, mode: InputChannel = "1") {
  if (!(await ensureWorklet(ctx))) return null;
  return new WorkletRecorder(ctx, stream, null, 0, mode);
}

/** Legacy fallback when AudioWorklet is unavailable. */
class ScriptRecorder implements ITrackRecorder {
  private source: MediaStreamAudioSourceNode;
  private processor: ScriptProcessorNode;
  private sink: GainNode;
  private chunks: Float32Array[][];
  private channels: number;
  private recording = false;
  private peaks = new PeakAccumulator();
  level = 0;

  private inGain: GainNode;
  private splitter: ChannelSplitterNode | null;
  constructor(private ctx: AudioContext, private stream: MediaStream, monitor: AudioNode | null, inputGainDb = 0, mode: InputChannel = "1") {
    this.source = ctx.createMediaStreamSource(stream);
    this.inGain = ctx.createGain();
    this.inGain.gain.value = dbToLin(inputGainDb);
    const r = routeInput(ctx, stream, this.source, this.inGain, mode);
    this.splitter = r.splitter;
    const inCh = r.channels;
    this.channels = inCh;
    this.chunks = Array.from({ length: inCh }, () => []);
    this.processor = ctx.createScriptProcessor(4096, inCh, inCh);
    this.processor.onaudioprocess = (e) => {
      let peak = 0;
      for (let ch = 0; ch < this.channels; ch++) {
        const data = e.inputBuffer.getChannelData(ch);
        for (let i = 0; i < data.length; i++) { const a = Math.abs(data[i]); if (a > peak) peak = a; }
        if (this.recording) this.chunks[ch].push(new Float32Array(data));
      }
      if (this.recording) this.peaks.push(e.inputBuffer.getChannelData(0)); // live waveform
      this.level = peak;
    };
    this.sink = ctx.createGain();
    this.sink.gain.value = 0;
    this.inGain.connect(this.processor);
    this.processor.connect(this.sink);
    this.sink.connect(ctx.destination);
    if (monitor) this.inGain.connect(monitor);
  }

  livePeaks(): LivePeaks { return { min: this.peaks.min, max: this.peaks.max, binSamples: this.peaks.binSamples }; }
  setInputGain(db: number) { this.inGain.gain.value = dbToLin(db); }

  begin() { this.chunks = Array.from({ length: this.channels }, () => []); this.peaks.reset(); this.recording = true; }

  finish(): Promise<AudioBuffer | null> {
    this.recording = false;
    const total = this.chunks[0].reduce((n, c) => n + c.length, 0);
    if (total === 0) return Promise.resolve(null);
    const buf = this.ctx.createBuffer(this.channels, total, this.ctx.sampleRate);
    for (let ch = 0; ch < this.channels; ch++) {
      const out = buf.getChannelData(ch);
      let off = 0;
      for (const c of this.chunks[ch]) { out.set(c, off); off += c.length; }
    }
    return Promise.resolve(buf);
  }

  dispose() {
    try { this.processor.onaudioprocess = null as any; } catch {}
    try { this.processor.disconnect(); } catch {}
    try { this.inGain.disconnect(); } catch {}
    try { this.source.disconnect(); } catch {}
    try { this.splitter?.disconnect(); } catch {}
    try { this.sink.disconnect(); } catch {}
  }
}

/** Mic capture mode. "raw" = no processing (Chrome on Windows opens the mic in
 *  WASAPI raw mode, bypassing driver/OS noise suppression and mic-array
 *  beamforming). "nr" = request noise suppression (the OS's own, e.g. Windows
 *  Voice Focus, where Chrome supports it — otherwise the browser's), closer to
 *  what DAWs on the default Windows audio path hear. */
export type MicProcessing = "raw" | "nr";

/** Input "device" id for the remote partner's microphone in a live session. It is
 *  only usable after the partner allowed recording for the current session. */
export const REMOTE_INPUT = "remote:partner";

export class RecorderManager {
  private recorders = new Map<string, ITrackRecorder>();
  private streams = new Map<string, MediaStream>();
  processing: MicProcessing = "nr";
  /** The partner's live audio, or null when there's no session / no permission. */
  remoteStream: (() => MediaStream | null) | null = null;

  constructor(private engine: AudioEngine) {}

  private async getStream(deviceId: string | null): Promise<MediaStream> {
    if (deviceId === REMOTE_INPUT) {
      // Never cached here: the stream belongs to the peer connection.
      const rs = this.remoteStream?.();
      if (!rs || !rs.getAudioTracks().some((t) => t.readyState === "live")) {
        const err = new Error("Your partner hasn't allowed recording in this session (or isn't connected).");
        err.name = "RemotePermissionError";
        throw err;
      }
      return rs;
    }
    const key = `${deviceId || "default"}|${this.processing}`;
    const cached = this.streams.get(key);
    if (cached && cached.getAudioTracks().some((t) => t.readyState === "live")) return cached;
    if (cached) { cached.getTracks().forEach((t) => t.stop()); this.streams.delete(key); }
    // Disable all browser DSP so we capture raw, full-dynamics audio. AGC in
    // particular acts as a compressor/limiter and flattens dynamics.
    const nr = this.processing === "nr";
    const noDsp = { echoCancellation: false, noiseSuppression: nr, autoGainControl: false } as const;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        channelCount: { ideal: 2 }, // open both inputs; the track's input-channel setting picks which to record
        ...noDsp,
        // some implementations only honor these under `advanced`
        advanced: [{ echoCancellation: false }, { noiseSuppression: nr }, { autoGainControl: false }],
      } as MediaTrackConstraints,
    });
    // Re-apply after opening — drivers sometimes ignore the initial request.
    for (const tr of stream.getAudioTracks()) {
      try { await tr.applyConstraints(noDsp as MediaTrackConstraints); } catch {}
    }
    this.streams.set(key, stream);
    return stream;
  }

  /** Reports whether the live input still has DSP (AGC/NS/EC) enabled despite our
   *  request — i.e. the OS/driver forced it on (which limits dynamics). */
  inputProcessing(): { agc: boolean; ns: boolean; ec: boolean } | null {
    for (const s of this.streams.values()) {
      for (const t of s.getAudioTracks()) {
        if (t.readyState !== "live") continue;
        const st = t.getSettings() as any;
        return { agc: st.autoGainControl === true, ns: st.noiseSuppression === true, ec: st.echoCancellation === true };
      }
    }
    return null;
  }

  /** Prepare recorders for the given armed tracks. Throws if a mic can't open. */
  async arm(tracks: { id: string; deviceId: string | null; monitor: boolean; inputGain?: number; inputChannel?: InputChannel }[]) {
    this.disposeAll();
    const useWorklet = await ensureWorklet(this.engine.ctx);
    for (const t of tracks) {
      const stream = await this.getStream(t.deviceId);
      const monitor = t.monitor ? this.engine.channelInput(t.id) ?? null : null;
      const mode = t.inputChannel ?? "1";
      const rec = useWorklet
        ? new WorkletRecorder(this.engine.ctx, stream, monitor, t.inputGain ?? 0, mode)
        : new ScriptRecorder(this.engine.ctx, stream, monitor, t.inputGain ?? 0, mode);
      this.recorders.set(t.id, rec);
    }
  }

  /** Update a track's input gain live (without re-arming). */
  setInputGain(trackId: string, db: number) { this.recorders.get(trackId)?.setInputGain(db); }

  count(): number { return this.recorders.size; }

  /** Best-effort input latency (seconds) from the mic track settings (often 0). */
  inputLatency(): number {
    let max = 0;
    for (const s of this.streams.values())
      for (const t of s.getAudioTracks()) {
        const l = (t.getSettings() as any).latency;
        if (typeof l === "number" && isFinite(l)) max = Math.max(max, l);
      }
    return max;
  }

  begin() { this.recorders.forEach((r) => r.begin()); }

  /** Audio-clock time of the first captured sample (earliest across tracks), if
   *  the capture reported it — lets takes be placed sample-accurately. */
  firstSampleTime(): number | null {
    let t: number | null = null;
    for (const r of this.recorders.values()) {
      const s = (r as { startedAt?: number | null }).startedAt;
      if (typeof s === "number" && (t == null || s < t)) t = s;
    }
    return t;
  }

  /** Stop and return captured buffers keyed by track. */
  async finish(): Promise<Map<string, AudioBuffer>> {
    const out = new Map<string, AudioBuffer>();
    const entries = Array.from(this.recorders.entries());
    const bufs = await Promise.all(entries.map(([, r]) => r.finish()));
    entries.forEach(([id], i) => { const b = bufs[i]; if (b) out.set(id, b); });
    return out;
  }

  levels(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [id, r] of this.recorders) out[id] = r.level;
    return out;
  }

  /** Live waveform peaks per recording track (for the recording-region overlay). */
  recordingPeaks(): Map<string, LivePeaks> {
    const out = new Map<string, LivePeaks>();
    for (const [id, r] of this.recorders) out.set(id, r.livePeaks());
    return out;
  }

  disposeAll() {
    this.recorders.forEach((r) => r.dispose());
    this.recorders.clear();
  }

  releaseStreams() {
    this.streams.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    this.streams.clear();
  }
}
